import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import styles from './OwnerLayout.module.css';

const NAV_ITEMS = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: 'grid',
    children: [
      { path: '/advanced-report', label: 'Advanced Report' },
    ],
  },
  { path: '/businesses', label: 'Businesses', icon: 'briefcase' },
  { path: '/reporters',  label: 'Reporters',  icon: 'users' },
  { path: '/entries',    label: 'Entries',    icon: 'file-text' },
  { path: '/expenses',   label: 'Expenses',   icon: 'dollar-sign' },
  { path: '/settings',   label: 'Settings',   icon: 'settings' },
];

const ICONS = {
  'grid': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  'briefcase': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  'users': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  'file-text': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  'dollar-sign': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  'settings': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
};

/** Returns the parent path whose group owns the current location. */
function activeParent(pathname) {
  for (const item of NAV_ITEMS) {
    if (!item.children) continue;
    const childPaths = item.children.map(c => c.path);
    if (
      pathname === item.path ||
      pathname.startsWith(item.path + '/') ||
      childPaths.includes(pathname)
    ) {
      return item.path;
    }
  }
  return null;
}

function OwnerLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  // Expanded state: keyed by parent path. Initialise open if we're already
  // inside that group.
  const [expanded, setExpanded] = useState(() => {
    const parent = activeParent(location.pathname);
    return parent ? { [parent]: true } : {};
  });

  // When the route changes (e.g. user navigates to /advanced-report from
  // another page), auto-open the parent group.
  useEffect(() => {
    const parent = activeParent(location.pathname);
    if (parent) {
      setExpanded(prev => ({ ...prev, [parent]: true }));
    }
  }, [location.pathname]);

  function toggleExpand(parentPath, e) {
    e.preventDefault(); // don't follow the NavLink href
    e.stopPropagation();
    setExpanded(prev => ({ ...prev, [parentPath]: !prev[parentPath] }));
  }

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <div className={styles.layout}>
      <aside className={styles.sidebar}>
        <div className={styles.logo}>
          <h2>Trucount</h2>
        </div>

        <nav className={styles.nav}>
          {NAV_ITEMS.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const isOpen      = hasChildren && !!expanded[item.path];
            const childPaths  = hasChildren ? item.children.map(c => c.path) : [];
            const groupActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + '/') ||
              childPaths.includes(location.pathname);

            return (
              <div key={item.path} className={styles.navGroup}>
                {/* Row: NavLink for navigation + chevron button for toggle */}
                <div className={`${styles.navItem} ${groupActive ? styles.navItemActive : ''}`}>
                  <NavLink
                    to={item.path}
                    className={styles.navItemLink}
                  >
                    <span className={styles.navIcon}>{ICONS[item.icon]}</span>
                    <span>{item.label}</span>
                  </NavLink>

                  {hasChildren && (
                    <button
                      className={`${styles.chevronBtn} ${isOpen ? styles.chevronOpen : ''}`}
                      onClick={(e) => toggleExpand(item.path, e)}
                      aria-label={isOpen ? 'Collapse' : 'Expand'}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Sub-menu */}
                {hasChildren && isOpen && (
                  <div className={styles.subNav}>
                    {item.children.map(child => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive }) =>
                          `${styles.subNavItem} ${isActive ? styles.subNavItemActive : ''}`
                        }
                      >
                        <span className={styles.subNavDot} />
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.userInfo}>
            <span className={styles.userEmail}>{user?.email}</span>
            <span className={styles.userTier}>{user?.profile?.subscription_tier || 'SOLO'}</span>
          </div>
          <button onClick={handleLogout} className={styles.logoutBtn}>
            Sign Out
          </button>
        </div>
      </aside>

      <main className={styles.main}>
        {children}
      </main>
    </div>
  );
}

export default OwnerLayout;
